import { ChangeLogDto, PagingOptions } from "@libs/api/dtos/index.ts";

export interface XeroTenantInfoDto {
  tenantId: string | undefined;
  tenants: XeroTenantDto[];
}

export interface XeroTenantDto {
  tenantId: string;
  tenantName: string;
  tenantType: string;
}

export interface XeroSettingsDto {
  targetTenantId: string;
  startDate?: string;
  includeInvoices?: boolean;
  includePayments?: boolean;
  hasSynced: boolean;
  changeLog: ChangeLogDto;
  eTag?: string;
}

export interface XeroSettingsUpdateDto
  extends Omit<XeroSettingsDto, "hasSynced" | "changeLog"> {}

export interface XeroSettingsAddDto
  extends Omit<XeroSettingsDto, "eTag" | "changeLog" | "hasSynced"> {}

interface XeroFinancialSyncBaseRequestDto {
  id: string;
  targetTenantId: string;
  status?: string;
  changeLog: ChangeLogDto;
  eTag?: string;
}

interface XeroFinancialStatusDetails {
  message?: string;
  exceptionMessage?: string;
  stackTrace?: string;
}

export interface XeroFinancialSyncAllRequestDto
  extends XeroFinancialSyncBaseRequestDto {
  startDate: Date;
}

export interface XeroFinancialSyncGetRequestDto
  extends XeroFinancialSyncBaseRequestDto {
  syncGetType?: string;
  statusDetails?: XeroFinancialStatusDetails[];
}

export interface XeroFetchedAccountDto {
  id: string;
  code?: string;
  name?: string;
  type?: string;
  status?: string;
  bankAccountNumber?: string;
  enablePaymentsToAccount?: boolean;
}

export interface XeroAccountMappingsDto {
  id: string;
  targetTenantId?: string;
  providerId?: string;
  invoiceAccountId?: string;
  paymentAccountId?: string;
  unallocatedAccountId?: string;
  changeLog: ChangeLogDto;
  eTag: string;
}

export interface XeroAccountMappingsAddDto
  extends Omit<XeroAccountMappingsDto, "id" | "eTag" | "changeLog"> {}

export interface XeroFinancialSyncGetRequestArgsDto {
  targetTenantId: string;
}

export enum XeroFinancialSyncStatus {
  Pending = "PENDING",
  InternalFailure = "INTERNALFAILURE",
  Completed = "COMPLETED",
  Warnings = "WARNINGS",
  Errors = "ERRORS",
  Success = "SUCCESS"
}

export interface GetXeroFinancialSyncDataFilters extends PagingOptions {
  targetTenantId?: string;
  statuses?: XeroFinancialSyncStatus[];
  sourceKeys?: string[];
}

type XeroOpreationType =
  | "XERO.CONTACT.CREATE"
  | "XERO.CONTACT.UPDATE"
  | "XERO.INVOICE.CREATE"
  | "XERO.INVOICE.UPDATE"
  | "XERO.CREDITNOTE.CREATE"
  | "XERO.ITEM.CREATE"
  | "XERO.ACCOUNT.CREATE"
  | "XERO.BATCHPAYMENT.CREATE"
  | "XERO.BATCHPAYMENT.DELETE"
  | "XERO.BANKTRANSACTION.CREATE"
  | "XERO.BANKTRANSACTION.UPDATE";

export interface XeroFinancialSyncOperation {
  id: string;
  financialSyncSourceId: string;
  sourceEntityId: string;
  financialSyncAllRequestId: string;
  correlationId: string;
  targetId: string;
  status: XeroFinancialSyncStatus;
  operationDate: string;
  operationType: XeroOpreationType;
  operationOrder: number;
  payload: {
    contact?: { sourceKey: string };
    date?: string;
    items?: Array<{
      description: string;
      quantity: 1;
      unitAmount: 99;
    }>;
    payloadType: string;
    status?: string;
    type?: string;
  };
  statusDetails: Array<{
    message: string;
    exceptionMessage: string;
    stackTrace: string;
  }>;
  predecessors: {
    sourceKey: string;
    operationType: XeroOpreationType;
  }[];
  changeLog: ChangeLogDto;
  eTag: string;
}

export interface XeroFinancialSyncSource {
  id: string;
  sourceKey: string;
  targetTenantId: string;
  operations: XeroFinancialSyncOperation[];
  eTag: string;
}

export interface XeroFinancialSyncData {
  id: string;
  sourceKey: string;
  sourceEntityId?: string;
  targetId?: string;
  targetTenantId: string;
  status: XeroFinancialSyncStatus;
  operationDate: string;
  operationType: XeroOpreationType;
  operationOrder: number;
  payload: {
    contact?: { sourceKey: string };
    date?: string;
    items?: Array<{
      description: string;
      quantity: 1;
      unitAmount: 99;
    }>;
    payloadType: string;
    status?: string;
    type?: string;
  };
  statusDetails: Array<{
    message: string;
    exceptionMessage: string;
    stackTrace: string;
  }>;
  predecessors: Array<{
    sourceKey: string;
    operationType: string;
  }>;
  changeLog: ChangeLogDto;
  eTag: string;
}
